/**
 * Feature Override Admin — Dev Console section for per-org feature flag mgmt.
 *
 * Lets a dev pick an org and toggle individual features on/off (override
 * stage default). Every change writes a `feature_override_audits` row.
 *
 * Three controls per feature:
 *   - "Default" — no override; stage default applies (mvp visible to all,
 *     base/deep hidden from consumers)
 *   - "Enabled" — feature is force-visible for this org
 *   - "Disabled" — feature is force-hidden for this org (rare, but useful)
 *
 * Reason is optional per change; surfaced in the audit log.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Shield, Search, X, ChevronDown } from '@/utils/icons';
import {
  listFeatureCatalog,
  listFeatureOverrides,
  listFeatureOverrideAudit,
  upsertFeatureOverride,
  removeFeatureOverride,
  type FeatureDefinitionDto,
  type FeatureOverrideRow,
  type FeatureOverrideAuditRow,
} from '@/services/api';
import type { Organization } from '@/types/tenant';

type OverrideValue = 'default' | 'enabled' | 'disabled';

interface FeatureOverrideAdminProps {
  organizations: Organization[];
}

export function FeatureOverrideAdmin({ organizations }: FeatureOverrideAdminProps) {
  const [features, setFeatures] = useState<FeatureDefinitionDto[]>([]);
  const [overrides, setOverrides] = useState<FeatureOverrideRow[]>([]);
  const [audit, setAudit] = useState<FeatureOverrideAuditRow[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const [orgPickerSearch, setOrgPickerSearch] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [reason, setReason] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  // Initial load — catalog + audit (overrides depend on selected org)
  useEffect(() => {
    let cancelled = false;
    Promise.all([listFeatureCatalog(), listFeatureOverrideAudit({ limit: 50 })])
      .then(([cat, aud]) => {
        if (cancelled) return;
        setFeatures(cat.features);
        setAudit(aud.audit);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load admin data');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch overrides whenever selected org changes
  useEffect(() => {
    setSelectedFeatureIds(new Set());
    if (!selectedOrgId) {
      setOverrides([]);
      return;
    }
    let cancelled = false;
    listFeatureOverrides({ organizationId: selectedOrgId })
      .then(({ overrides: rows }) => {
        if (!cancelled) setOverrides(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load overrides');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOrgId]);

  // Full list of orgs filtered by the in-picker search box. No 100-cap —
  // long lists are handled by an overflow-y-auto container in the popover.
  const filteredOrgs = useMemo(() => {
    if (!orgPickerSearch.trim()) return organizations;
    const needle = orgPickerSearch.toLowerCase();
    return organizations.filter(
      (o) => o.name.toLowerCase().includes(needle) || o.id.toLowerCase().includes(needle)
    );
  }, [organizations, orgPickerSearch]);

  // Filtered audit list — substring match on org name or feature id.
  const filteredAudit = useMemo(() => {
    if (!auditSearch.trim()) return audit;
    const needle = auditSearch.toLowerCase();
    return audit.filter(
      (a) =>
        (a.organizationName ?? '').toLowerCase().includes(needle) ||
        a.organizationId.toLowerCase().includes(needle) ||
        a.featureId.toLowerCase().includes(needle) ||
        a.actorEmail.toLowerCase().includes(needle)
    );
  }, [audit, auditSearch]);

  const currentValue = (featureId: string): OverrideValue => {
    const row = overrides.find((o) => o.featureId === featureId);
    if (!row) return 'default';
    return row.enabled ? 'enabled' : 'disabled';
  };

  const handleChange = async (feature: FeatureDefinitionDto, value: OverrideValue) => {
    if (!selectedOrgId) return;
    setError(null);
    setSavingKey(feature.id);
    try {
      if (value === 'default') {
        await removeFeatureOverride(selectedOrgId, feature.id);
      } else {
        await upsertFeatureOverride({
          organizationId: selectedOrgId,
          featureId: feature.id,
          enabled: value === 'enabled',
          reason: reason.trim() || undefined,
        });
      }
      // Refresh both overrides + audit (latest entry will be from this change)
      const [{ overrides: rows }, { audit: aud }] = await Promise.all([
        listFeatureOverrides({ organizationId: selectedOrgId }),
        listFeatureOverrideAudit({ limit: 50 }),
      ]);
      setOverrides(rows);
      setAudit(aud);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save override');
    } finally {
      setSavingKey(null);
    }
  };

  const toggleFeatureSelect = (id: string) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleFeatureSelectAll = () => {
    if (selectedFeatureIds.size === features.length) {
      setSelectedFeatureIds(new Set());
    } else {
      setSelectedFeatureIds(new Set(features.map((f) => f.id)));
    }
  };

  // Apply one override value to every selected feature. Per-feature failures
  // are tolerated and summarized; the shared reason field applies to all.
  const handleBulkChange = async (value: OverrideValue) => {
    if (!selectedOrgId || selectedFeatureIds.size === 0) return;
    setError(null);
    setBulkSaving(true);
    // "Default" only needs a remove call for features that actually have an
    // override — the rest are already at the stage default.
    const ids = Array.from(selectedFeatureIds).filter(
      (fid) => value !== 'default' || overrides.some((o) => o.featureId === fid)
    );
    const results = await Promise.allSettled(
      ids.map((fid) =>
        value === 'default'
          ? removeFeatureOverride(selectedOrgId, fid)
          : upsertFeatureOverride({
              organizationId: selectedOrgId,
              featureId: fid,
              enabled: value === 'enabled',
              reason: reason.trim() || undefined,
            })
      )
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) setError(`${failed} of ${ids.length} bulk changes failed`);
    try {
      const [{ overrides: rows }, { audit: aud }] = await Promise.all([
        listFeatureOverrides({ organizationId: selectedOrgId }),
        listFeatureOverrideAudit({ limit: 50 }),
      ]);
      setOverrides(rows);
      setAudit(aud);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh overrides');
    }
    setSelectedFeatureIds(new Set());
    setBulkSaving(false);
  };

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

  const stageBadgeClass = (stage: FeatureDefinitionDto['stage']) => {
    switch (stage) {
      case 'mvp':
        return 'dls-bg-status-online/15 dls-text-status-online dls-border-status-online/30';
      case 'base':
        return 'dls-bg-neeve-primary/10 dls-text-neeve-primary dls-border-neeve-primary/30';
      case 'deep':
        return 'dls-bg-neeve-orange/15 dls-text-neeve-orange dls-border-neeve-orange/30';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-2">
          <Shield className="dls-h-5 dls-w-5 dls-text-neeve-primary" />
          Feature Overrides
        </CardTitle>
        <p className="dls-text-xs dls-text-muted-foreground dls-mt-1">
          Per-org override of stage defaults. Use to unlock paid Deep Insights features for a paying
          tenant or grant early access to a base-stage feature. Every change is audit-logged.
        </p>
      </CardHeader>
      <CardContent className="dls-space-y-4">
        {error && (
          <div className="dls-rounded-md dls-border dls-border-status-offline/30 dls-bg-status-offline/10 dls-px-3 dls-py-2 dls-text-xs dls-text-status-offline">
            {error}
          </div>
        )}

        {/* Org picker (searchable) + Reason input — side by side, equal width.
            Stacks on mobile. */}
        <div className="dls-flex dls-flex-col dls-gap-2 md:dls-flex-row md:dls-items-center">
          {/* Searchable org picker — Popover with an Input + scrollable list */}
          <Popover open={orgPickerOpen} onOpenChange={setOrgPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                className="dls-h-8 md:dls-flex-1 dls-justify-between dls-font-normal"
                role="combobox"
                aria-expanded={orgPickerOpen}
              >
                <span className={selectedOrg ? 'dls-text-foreground' : 'dls-text-muted-foreground'}>
                  {selectedOrg?.name ?? 'Select an organization'}
                </span>
                <ChevronDown className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-flex-shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="dls-w-[min(28rem,90vw)] dls-p-0" align="start">
              <div className="dls-p-2 dls-border-b dls-border-border">
                <div className="dls-relative">
                  <Search className="dls-absolute dls-left-2.5 dls-top-1/2 dls--translate-y-1/2 dls-h-3.5 dls-w-3.5 dls-text-muted-foreground dls-pointer-events-none" />
                  <Input
                    value={orgPickerSearch}
                    onChange={(e) => setOrgPickerSearch(e.target.value)}
                    placeholder={`Search ${organizations.length} organizations…`}
                    className="dls-h-8 dls-pl-8 dls-text-sm"
                    autoFocus
                  />
                </div>
              </div>
              <div className="dls-max-h-80 dls-overflow-y-auto dls-py-1">
                {filteredOrgs.length === 0 ? (
                  <div className="dls-px-3 dls-py-4 dls-text-sm dls-text-muted-foreground dls-text-center">
                    No matching organizations
                  </div>
                ) : (
                  filteredOrgs.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => {
                        setSelectedOrgId(o.id);
                        setOrgPickerOpen(false);
                      }}
                      className="dls-w-full dls-flex dls-items-center dls-justify-between dls-px-3 dls-py-1.5 dls-text-sm dls-text-left hover:dls-bg-muted dls-cursor-pointer"
                    >
                      <span className="dls-truncate">{o.name}</span>
                      {o.id === selectedOrgId && (
                        <span className="dls-text-xs dls-text-neeve-primary dls-font-medium">selected</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Reason input — applied to next change */}
          <div className="dls-relative md:dls-flex-1">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional reason for the next change (e.g., 'Deep Insights contract signed')"
              className="dls-h-8 dls-text-sm dls-w-full dls-pr-8"
            />
            {reason && (
              <button
                type="button"
                onClick={() => setReason('')}
                aria-label="Clear reason"
                className="dls-absolute dls-right-2 dls-top-1/2 dls--translate-y-1/2 dls-text-muted-foreground hover:dls-text-foreground"
              >
                <X className="dls-h-3.5 dls-w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Per-feature controls for selected org */}
        {selectedOrg && (
          <>
            {/* Bulk override bar — apply one value to all selected features */}
            <div className="dls-flex dls-flex-wrap dls-items-center dls-gap-2">
              <span className="dls-text-sm dls-text-muted-foreground">
                {selectedFeatureIds.size > 0
                  ? `${selectedFeatureIds.size} selected`
                  : `${features.length} features`}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={selectedFeatureIds.size === 0 || bulkSaving}
                onClick={() => handleBulkChange('enabled')}
              >
                {bulkSaving ? 'Applying...' : 'Enable Selected'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={selectedFeatureIds.size === 0 || bulkSaving}
                onClick={() => handleBulkChange('disabled')}
              >
                Disable Selected
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={selectedFeatureIds.size === 0 || bulkSaving}
                onClick={() => handleBulkChange('default')}
              >
                Reset to Default
              </Button>
            </div>
            <div className="dls-rounded-md dls-border dls-border-border dls-overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="dls-w-10">
                    <input
                      type="checkbox"
                      checked={features.length > 0 && selectedFeatureIds.size === features.length}
                      onChange={toggleFeatureSelectAll}
                      className="dls-rounded dls-border-border"
                    />
                  </TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead className="dls-w-24">Stage</TableHead>
                  <TableHead className="dls-w-24">Scope</TableHead>
                  <TableHead className="dls-w-48">Override</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((f) => {
                  const value = currentValue(f.id);
                  const saving = savingKey === f.id;
                  return (
                    <TableRow key={f.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedFeatureIds.has(f.id)}
                          onChange={() => toggleFeatureSelect(f.id)}
                          className="dls-rounded dls-border-border"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="dls-flex dls-flex-col">
                          <span className="dls-font-medium dls-text-foreground">{f.label}</span>
                          <span className="dls-text-xs dls-font-mono dls-text-muted-foreground">{f.id}</span>
                          {f.description && (
                            <span className="dls-text-xs dls-text-muted-foreground dls-mt-0.5">{f.description}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`dls-inline-flex dls-items-center dls-rounded dls-border dls-px-1.5 dls-py-0.5 dls-text-[10px] dls-font-semibold dls-tracking-wider dls-uppercase ${stageBadgeClass(f.stage)}`}>
                          {f.stage}
                        </span>
                      </TableCell>
                      <TableCell className="dls-text-xs dls-text-muted-foreground">{f.scope}</TableCell>
                      <TableCell>
                        <Select
                          value={value}
                          onValueChange={(v) => handleChange(f, v as OverrideValue)}
                          disabled={saving || bulkSaving}
                        >
                          <SelectTrigger className="dls-h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Default (stage rule)</SelectItem>
                            <SelectItem value="enabled">Enabled (force-on)</SelectItem>
                            <SelectItem value="disabled">Disabled (force-off)</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </>
        )}

        {/* Recent audit entries */}
        <div className="dls-pt-2">
          <div className="dls-flex dls-flex-col dls-gap-2 sm:dls-flex-row sm:dls-items-center sm:dls-justify-between dls-mb-2">
            <div className="dls-flex dls-items-center dls-gap-2">
              <div className="dls-text-xs dls-font-semibold dls-uppercase dls-tracking-wider dls-text-muted-foreground/70">
                Recent Changes
              </div>
              <Badge variant="secondary" className="dls-text-[10px]">
                {filteredAudit.length === audit.length
                  ? `last ${audit.length}`
                  : `${filteredAudit.length} of ${audit.length}`}
              </Badge>
            </div>
            {audit.length > 0 && (
              <div className="dls-relative sm:dls-w-72">
                <Search className="dls-absolute dls-left-2.5 dls-top-1/2 dls--translate-y-1/2 dls-h-3.5 dls-w-3.5 dls-text-muted-foreground dls-pointer-events-none" />
                <Input
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  placeholder="Filter by org, feature, or actor…"
                  className="dls-h-7 dls-pl-8 dls-pr-8 dls-text-xs"
                />
                {auditSearch && (
                  <button
                    type="button"
                    onClick={() => setAuditSearch('')}
                    aria-label="Clear audit filter"
                    className="dls-absolute dls-right-2 dls-top-1/2 dls--translate-y-1/2 dls-text-muted-foreground hover:dls-text-foreground"
                  >
                    <X className="dls-h-3 dls-w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
          {filteredAudit.length === 0 ? (
            <div className="dls-py-4 dls-text-center dls-text-xs dls-text-muted-foreground">
              {audit.length === 0
                ? 'No override changes recorded yet.'
                : 'No entries match your filter.'}
            </div>
          ) : (
            <div className="dls-rounded-md dls-border dls-border-border dls-overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="dls-w-44">When</TableHead>
                    <TableHead>Org</TableHead>
                    <TableHead>Feature</TableHead>
                    <TableHead className="dls-w-32">Change</TableHead>
                    <TableHead>Actor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAudit.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="dls-font-mono dls-text-xs">{new Date(a.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{a.organizationName ?? a.organizationId}</TableCell>
                      <TableCell className="dls-font-mono dls-text-xs">{a.featureId}</TableCell>
                      <TableCell className="dls-text-xs">
                        {a.previousValue ?? '—'} → <strong>{a.newValue}</strong>
                      </TableCell>
                      <TableCell className="dls-text-xs dls-text-muted-foreground">{a.actorEmail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
